// @flow

import { GATSBY_C21_IMAGES_BASEPATH } from "../../env";

type ImageNode = {
    path: string,
    filename: string,
    extension: string,
    lastModifiedDate?: ?string,
};

type Edits = {
    resize: {
        withoutEnlargement: boolean,
        height?: *,
        width?: *,
    },
};

const IMAGE_QUALITY = 75;

const fixed = (image: ImageNode, { width, height }: *) => {
    const fixedImageSizes = [1, 2, 3, 4];

    const createSourceSetEntries = (
        image: ImageNode,
        width: number,
        height: number,
    ) =>
        fixedImageSizes
            .map((size) => ({
                width: width * size,
                height: height * size,
                size,
            }))
            .map(({ width, height, size }: *) => {
                const format = image.extension.substring(1);
                return {
                    src: createPath(image, {
                        format: format,
                        quality: IMAGE_QUALITY,
                        width: width,
                        height: height,
                    }),
                    webp: createPath(image, {
                        format: "webp",
                        quality: IMAGE_QUALITY,
                        width: width,
                        height: height,
                    }),
                    size,
                };
            });

    const toSourceSet = (entries: *, getValue: *) => ({
        src: getValue(entries[0]),
        srcSet: entries
            .map((entry) => `${getValue(entry)} ${entry.size}x`)
            .join(", "),
    });

    const createSourceSets = (imageNode: *, width: *, height: *) => {
        const sourceSetsEntries = createSourceSetEntries(
            imageNode,
            width,
            height,
        );

        const src = toSourceSet(sourceSetsEntries, (entry) => entry.src);
        const webp = toSourceSet(sourceSetsEntries, (entry) => entry.webp);

        return {
            width,
            height,
            src: src.src,
            srcSet: src.srcSet,
            srcWebp: webp.src,
            srcSetWebp: webp.srcSet,
        };
    };

    return {
        width,
        height,
        ...createSourceSets(image, width, height),
    };
};

const fluid = (image: *, { maxWidth }: *) => {
    const fluidImageSizes = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4];

    const createSourceSetEntries = (image: *, maxWidth: *) => {
        const entries = fluidImageSizes.map((size) => {
            const width = Math.ceil(maxWidth * size);
            return {
                width: width,
                size,
            };
        });
        return entries.map(({ width }: *) => {
            const result = {
                src: createPath(image, {
                    format: "jpeg",
                    quality: IMAGE_QUALITY,
                    width: width,
                    height: 0,
                }),
                webp: createPath(image, {
                    format: "webp",
                    quality: IMAGE_QUALITY,
                    width: width,
                    height: 0,
                }),
                max: width,
            };
            return result;
        });
    };

    const toSourceSet = (entries: *, getValue: *) => ({
        src: getValue(entries[0]),
        srcSet: entries
            .map((entry) => `${getValue(entry)} ${entry.max}w`)
            .join(", "),
    });

    const createSourceSets = (imageNode: *, maxWidth: *) => {
        const sourceSetsEntries = createSourceSetEntries(imageNode, maxWidth);

        const src = toSourceSet(sourceSetsEntries, (entry) => entry.src);
        const webp = toSourceSet(sourceSetsEntries, (entry) => entry.webp);

        return {
            src: src.src,
            srcSet: src.srcSet,
            srcWebp: webp.src,
            srcSetWebp: webp.srcSet,
            sizes: `(max-width: ${maxWidth}px), 100vw, ${maxWidth}px`,
            aspectRatio: 16 / 9, // TODO [TKN-20200629] Get from API instead of hardcoded aspect ratio
        };
    };

    return {
        maxWidth,
        ...createSourceSets(image, maxWidth),
    };
};

const resolveGatsbyImageData = (image: *, options: *) => {
    if (!options?.layout || options.layout === "CONSTRAINED") {
        const resolvedFluid = fluid(image, { maxWidth: options.width });

        return {
            layout: "constrained",
            width: options.width,
            height: options.height,
            backgroundColor: "#e8e8e8",

            images: {
                sources: [
                    {
                        srcSet: resolvedFluid.srcSetWebp,
                        sizes: `(min-width: ${options.width}px) ${options.width}px, 100vw`,
                        type: "image/webp",
                    },
                ],
                fallback: {
                    src: resolvedFluid.src,
                    srcSet: resolvedFluid.srcSet,
                    sizes: `(min-width: ${options.width}px) ${options.width}px, 100vw`,
                },
            },
        };
    }

    if (options?.layout === "FIXED") {
        const resolvedFixed = fixed(image, options);

        return {
            layout: "fixed",
            width: options.width,
            height: options.height,
            backgroundColor: "#e8e8e8",

            images: {
                sources: [
                    {
                        srcSet: resolvedFixed.srcSetWebp,
                        sizes: `(min-width: ${options.width}px) ${options.width}px, 100vw`,
                        type: "image/webp",
                    },
                ],
                fallback: {
                    src: resolvedFixed.src,
                    srcSet: resolvedFixed.srcSet,
                    sizes: `(min-width: ${options.width}px) ${options.width}px, 100vw`,
                },
            },
        };
    }

    return {
        images: {
            sources: [
                {
                    srcSet: "https://images.ctfassets.net/3mblvi7gubtq/3clwk7Q7xyH7ZNjl1y2sey/2038c573974b9de1b05ef9f7b53b91c5/businessman-collaborate-collaboration-872957.jpg?w=586&h=423&q=80&fm=webp 586w,\nhttps://images.ctfassets.net/3mblvi7gubtq/3clwk7Q7xyH7ZNjl1y2sey/2038c573974b9de1b05ef9f7b53b91c5/businessman-collaborate-collaboration-872957.jpg?w=1172&h=847&q=80&fm=webp 1172w,\nhttps://images.ctfassets.net/3mblvi7gubtq/3clwk7Q7xyH7ZNjl1y2sey/2038c573974b9de1b05ef9f7b53b91c5/businessman-collaborate-collaboration-872957.jpg?w=2344&h=1693&q=80&fm=webp 2344w,\nhttps://images.ctfassets.net/3mblvi7gubtq/3clwk7Q7xyH7ZNjl1y2sey/2038c573974b9de1b05ef9f7b53b91c5/businessman-collaborate-collaboration-872957.jpg?w=4000&h=2889&q=80&fm=webp 4000w",
                    sizes: "(min-width: 2344px) 2344px, 100vw",
                    type: "image/webp",
                },
            ],
            fallback: {
                src: "https://images.ctfassets.net/3mblvi7gubtq/3clwk7Q7xyH7ZNjl1y2sey/2038c573974b9de1b05ef9f7b53b91c5/businessman-collaborate-collaboration-872957.jpg?w=2344&h=1693&fl=progressive&q=80&fm=jpg",
                srcSet: "https://images.ctfassets.net/3mblvi7gubtq/3clwk7Q7xyH7ZNjl1y2sey/2038c573974b9de1b05ef9f7b53b91c5/businessman-collaborate-collaboration-872957.jpg?w=586&h=423&fl=progressive&q=80&fm=jpg 586w,\nhttps://images.ctfassets.net/3mblvi7gubtq/3clwk7Q7xyH7ZNjl1y2sey/2038c573974b9de1b05ef9f7b53b91c5/businessman-collaborate-collaboration-872957.jpg?w=1172&h=847&fl=progressive&q=80&fm=jpg 1172w,\nhttps://images.ctfassets.net/3mblvi7gubtq/3clwk7Q7xyH7ZNjl1y2sey/2038c573974b9de1b05ef9f7b53b91c5/businessman-collaborate-collaboration-872957.jpg?w=2344&h=1693&fl=progressive&q=80&fm=jpg 2344w,\nhttps://images.ctfassets.net/3mblvi7gubtq/3clwk7Q7xyH7ZNjl1y2sey/2038c573974b9de1b05ef9f7b53b91c5/businessman-collaborate-collaboration-872957.jpg?w=4000&h=2889&fl=progressive&q=80&fm=jpg 4000w",
                sizes: "(min-width: 2344px) 2344px, 100vw",
            },
        },
        layout: "constrained",
        backgroundColor: "#e8e8e8",
        width: 2344,
        height: 1693,
    };
};

const ImageMapper = {
    toFixed: fixed,
    toFluid: fluid,
    toGatsbyImageData: resolveGatsbyImageData,
};

export default ImageMapper;

const createPath = (
    { path, filename, extension, lastModifiedDate }: ImageNode,
    editConfig,
) => {
    const basePath = GATSBY_C21_IMAGES_BASEPATH;
    const optionsObject = {
        key: `${path}/${filename}${extension}`,
        edits: edits(editConfig),
        lastModifiedDate,
    };

    try {
        const optionsString = JSON.stringify(optionsObject);
        // add extra escape for image names with special characters & spaces
        const base64 = btoa(unescape(encodeURIComponent(optionsString)));
        return `${basePath}/${base64}`;
    } catch (error) {
        return error;
    }
};

const edits = ({ format, quality, width, height }: *) => {
    const requestFormat: string = /jpg/i.test(format) ? "jpeg" : format;

    const edits: Edits = {
        [requestFormat]: {
            quality: quality,
        },
        rotate: null,
        resize: {
            withoutEnlargement: true,
        },
    };

    if (width) {
        edits.resize.width = width;
    }

    if (height) {
        edits.resize.height = height;
    }

    return edits;
};
